import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  IDLE_WARNING_MS,
  idleStateAt,
  secondsBeforeLogout,
  type IdleState,
} from '@domain/policies/SessionSecurityPolicy';
import { getServices } from '@infrastructure/services';
import { markSessionEnded } from '../sessionEnd';
import { useRuntimeConfig } from './RuntimeConfigProvider';
import { useSessionState } from './SessionProvider';
import styles from './IdleLogout.module.css';

/**
 * Cierra la sesión sola cuando nadie la usa.
 *
 * No había nada: Firebase persiste la sesión en el navegador y nadie había puesto
 * otra cosa, así que no caducaba **nunca**. Un portátil abierto en una oficina
 * abierta era una ficha de personal abierta, y 48 de las 339 personas ven datos
 * de otras — los contactos de emergencia de su equipo, el expediente completo, el
 * sueldo.
 *
 * **La última actividad se guarda en el navegador, no en memoria**, y ése es el
 * caso que de verdad importa: un temporizador vivo sólo protege mientras la
 * pestaña está abierta, y el portátil que preocupa es el que se cerró anoche y se
 * abre esta mañana. Al arrancar se compara contra el reloj, y si la sesión estuvo
 * abandonada más del límite se cierra antes de pintar nada.
 *
 * **Y avisa un minuto antes.** Cerrar sin avisar sobre un formulario a medio
 * llenar convierte una medida de seguridad en pérdida de trabajo, que es como se
 * ganan los permisos para desactivarla.
 */

/** Dónde se anota el último gesto. Compartido entre pestañas a propósito. */
const STORAGE_KEY = 'knpeople.lastActivity';

/** Cada cuánto se comprueba. Un segundo: la cuenta atrás se pinta en segundos. */
const TICK_MS = 1_000;

/*
 * Lo que cuenta como actividad.
 *
 * `pointerdown` y no `pointermove`: mover el ratón al pasar por delante del
 * escritorio no es usar el portal. `visibilitychange` está para volver a mirar el
 * reloj al recuperar la pestaña, no para contar como gesto.
 */
const EVENTS: readonly string[] = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

const readLastActivity = (): number => {
  const raw = window.localStorage.getItem(STORAGE_KEY);
  const parsed = raw === null ? Number.NaN : Number(raw);
  return Number.isFinite(parsed) ? parsed : Date.now();
};

export const IdleLogoutProvider = ({ children }: { readonly children: ReactNode }): ReactNode => {
  const config = useRuntimeConfig();
  const idleLimitMs = config.rules.idleMinutes * 60_000;
  const { status } = useSessionState();

  /*
   * Sólo cuenta con una sesión abierta.
   *
   * Este proveedor envuelve la pantalla de acceso también, y corría ahí: tras 29
   * minutos quieto en «Entra con tu cuenta de Google» pintaba «Tu sesión está por
   * cerrarse» a alguien que no había entrado. Con el aviso de sesión cerrada
   * encima, además le habría dicho que la cerramos por inactividad.
   */
  const haySesion = status.kind !== 'signed-out' && status.kind !== 'loading';

  const [state, setState] = useState<IdleState>('active');
  const [seconds, setSeconds] = useState(0);
  /* La sesión se cierra una vez. Sin esto, cada segundo llamaría a `signOut`. */
  const cerrando = useRef(false);

  useEffect(() => {
    if (!haySesion) {
      setState('active');
      return;
    }

    /*
     * Cada sesión nueva vuelve a poder cerrarse.
     *
     * La marca se ponía a `true` en el primer cierre y nunca volvía a `false`, y
     * este proveedor no se desmonta al cerrar sesión. Quien volvía a entrar sin
     * recargar la pestaña se quedaba **sin cierre por inactividad** hasta la
     * siguiente recarga: la guarda contra llamar a `signOut` cada segundo
     * bloqueaba también todos los cierres siguientes.
     */
    cerrando.current = false;

    const marcar = (): void => {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
      setState('active');
    };

    /* Al montar cuenta como actividad sólo si no venía caducada: si el portátil
       estuvo cerrado, marcar aquí borraría la prueba de que estuvo cerrado. */
    if (window.localStorage.getItem(STORAGE_KEY) === null) marcar();

    for (const event of EVENTS) window.addEventListener(event, marcar, { passive: true });

    const handle = window.setInterval(() => {
      const ultima = readLastActivity();
      const ahora = Date.now();
      const siguiente = idleStateAt(ultima, ahora, idleLimitMs);

      setState(siguiente);
      setSeconds(secondsBeforeLogout(ultima, ahora, idleLimitMs));

      if (siguiente === 'expired' && !cerrando.current) {
        cerrando.current = true;
        window.localStorage.removeItem(STORAGE_KEY);
        /* Antes de cerrar, para que la pantalla de acceso sepa por qué llegó ahí. */
        markSessionEnded('idle');
        void getServices().auth.signOut();
      }
    }, TICK_MS);

    return () => {
      for (const event of EVENTS) window.removeEventListener(event, marcar);
      window.clearInterval(handle);
    };
  }, [idleLimitMs, haySesion]);

  return (
    <>
      {children}
      {state === 'warning' && (
        <div className={styles.warning} role="status" aria-live="polite">
          <strong className={styles.warningTitle}>Tu sesión está por cerrarse</strong>
          <span className={styles.warningNote}>
            Llevas {config.rules.idleMinutes} minutos sin actividad. Se cerrará en{' '}
            {seconds} {seconds === 1 ? 'segundo' : 'segundos'}; mueve el ratón o pulsa una
            tecla para seguir.
          </span>
        </div>
      )}
    </>
  );
};

/** Lo que la cuenta atrás enseña, para quien quiera pintarla en otro sitio. */
export const IDLE_WARNING_SECONDS = IDLE_WARNING_MS / 1000;
